// ============================================================
// Wauvea WebXR — Zone Panel
// Individual zone panel (merch, ad, graphic, side screen)
// ============================================================

import * as THREE from 'three';
import { CONFIG } from '../config.js';

export class ZonePanel {
  constructor(zoneConfig, index) {
    this.config = zoneConfig;
    this.index = index;
    this.mesh = null;
    this.isHovered = false;
  }

  /**
   * Build and return the panel mesh
   * @param {THREE.Texture|null} texture
   * @returns {THREE.Mesh}
   */
  build(texture = null) {
    const { angle, type, label, id } = this.config;

    // Per-zone dimensions (fall back to sideScreens defaults)
    const zoneWidth = this.config.width || 5;
    const zoneHeight = this.config.height || CONFIG.sideScreens.height;
    const zoneY = this.config.yPosition !== undefined ? this.config.yPosition : CONFIG.sideScreens.yPosition;
    const radius = CONFIG.sideScreens.radius;

    const angleRad = THREE.MathUtils.degToRad(angle || 0);
    const panelColor = CONFIG.zoneColors[type] || 0x6C5CE7;

    // Panel geometry
    const geo = new THREE.PlaneGeometry(zoneWidth, zoneHeight);

    // Material
    const mat = texture
      ? new THREE.MeshStandardMaterial({
          map: texture.clone(),
          emissive: new THREE.Color(panelColor),
          emissiveIntensity: 0.1,
          roughness: 0.5,
          metalness: 0.15,
          transparent: true,
          opacity: 0.92,
        })
      : new THREE.MeshStandardMaterial({
          color: panelColor,
          emissive: new THREE.Color(panelColor),
          emissiveIntensity: 0.2,
          roughness: 0.5,
          metalness: 0.15,
        });

    if (texture && mat.map) {
      mat.map.needsUpdate = true;
    }

    this.mesh = new THREE.Mesh(geo, mat);

    if (this.config.overhead) {
      // Overhead zone: large ceiling panel extending the sky
      this.mesh.position.set(0, 17, -5);
      this.mesh.rotation.x = Math.PI / 2;
    } else {
      // Position around dome at zone-specific Y height
      this.mesh.position.set(
        Math.sin(angleRad) * radius,
        zoneY,
        -Math.cos(angleRad) * radius
      );

      // Face center
      this.mesh.lookAt(0, zoneY, 0);
    }

    // Metadata
    this.mesh.userData = {
      zoneId: id,
      type,
      label,
      isClickable: true,
      baseEmissiveIntensity: texture ? 0.1 : 0.2,
    };
    this.mesh.name = `zone_${id}`;

    // Zone label above panel
    this._addLabel(label, type, zoneWidth, zoneHeight);

    return this.mesh;
  }

  _addLabel(text, type, panelWidth, panelHeight) {
    // High-res canvas for sharp text in VR (2x resolution)
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');

    // Background pill
    ctx.fillStyle = 'rgba(8, 8, 20, 0.75)';
    ctx.beginPath();
    ctx.roundRect(40, 16, 944, 128, 24);
    ctx.fill();

    // Type color accent line
    const colors = { graphic: '#6C5CE7', merch: '#FF6B9D', ad: '#00D2FF', side: '#FECA57' };
    ctx.fillStyle = colors[type] || '#6C5CE7';
    ctx.beginPath();
    ctx.roundRect(40, 16, 8, 128, [24, 0, 0, 24]);
    ctx.fill();

    // Text
    ctx.fillStyle = '#E0E0F0';
    ctx.font = '600 56px Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text.toUpperCase(), 512, 80);

    const labelTexture = new THREE.CanvasTexture(canvas);
    labelTexture.colorSpace = THREE.SRGBColorSpace;
    labelTexture.anisotropy = 4;  // Sharper at viewing angles

    const labelGeo = new THREE.PlaneGeometry(panelWidth * 0.75, 1.4);
    const labelMat = new THREE.MeshBasicMaterial({
      map: labelTexture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    const labelMesh = new THREE.Mesh(labelGeo, labelMat);
    // For overhead: same edge position, just pushed slightly below surface so it doesn't clip
    if (this.config.overhead) {
      labelMesh.position.y = panelHeight / 2 + 1;
      labelMesh.position.z = 0.3;
    } else {
      labelMesh.position.y = panelHeight / 2 + 1;
    }
    this.mesh.add(labelMesh);
  }

  setHovered(hovered) {
    if (!this.mesh) return;
    this.isHovered = hovered;
    const intensity = hovered ? 0.5 : this.mesh.userData.baseEmissiveIntensity;
    this.mesh.material.emissiveIntensity = intensity;
  }

  /**
   * Load a real media asset onto this zone panel
   * @param {string} url - Image or video URL
   * @param {'image'|'video'|'stream'} kind
   */
  setMedia(url, kind = 'image') {
    if (!this.mesh) return;

    if (kind === 'video' || kind === 'stream') {
      this._loadVideoTexture(url);
    } else {
      this._loadImageTexture(url);
    }
  }

  _loadImageTexture(url) {
    const loader = new THREE.TextureLoader();
    loader.load(url, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      this.mesh.material.map = texture;
      this.mesh.material.emissiveMap = texture;
      this.mesh.material.emissiveIntensity = 0.3;
      this.mesh.material.needsUpdate = true;
    }, undefined, (err) => {
      console.warn(`[ZonePanel] Failed to load image for ${this.config.id}:`, err);
    });
  }

  _loadVideoTexture(url) {
    // Clean up previous video if any
    if (this._videoElement) {
      this._videoElement.pause();
      this._videoElement.src = '';
    }

    const video = document.createElement('video');
    video.src = url;
    // Only set crossOrigin for absolute cross-origin URLs
    if (url.startsWith('http://') || url.startsWith('https://')) {
      video.crossOrigin = 'anonymous';
    }
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.load();
    video.play().catch(() => {});

    this._videoElement = video;
    this._videoTexture = new THREE.VideoTexture(video);
    this._videoTexture.colorSpace = THREE.SRGBColorSpace;
    this._videoTexture.minFilter = THREE.LinearFilter;

    this.mesh.material.map = this._videoTexture;
    this.mesh.material.emissiveMap = this._videoTexture;
    this.mesh.material.emissiveIntensity = 0.3;
    this.mesh.material.needsUpdate = true;
  }

  /**
   * Clear the loaded media and revert to placeholder
   */
  clearMedia() {
    if (!this.mesh) return;

    if (this._videoElement) {
      this._videoElement.pause();
      this._videoElement.src = '';
      this._videoElement = null;
    }
    this._videoTexture = null;

    const panelColor = CONFIG.zoneColors[this.config.type] || 0x6C5CE7;
    this.mesh.material.map = null;
    this.mesh.material.emissiveMap = null;
    this.mesh.material.emissive = new THREE.Color(panelColor);
    this.mesh.material.emissiveIntensity = 0.2;
    this.mesh.material.needsUpdate = true;
  }

  // ─── Corner Rounding ───────────────────────────────────────────
  /**
   * Apply rounded corners to this zone panel via an alpha mask texture.
   * Uses the same technique as MainScreen.setCornerRadius().
   * @param {number} radius — 0 = sharp, 0.1–5 = graduated rounding
   */
  setCornerRadius(radius) {
    if (!this.mesh) return;
    this._cornerRadius = radius;

    if (radius <= 0) {
      this.mesh.material.alphaMap = null;
      this.mesh.material.alphaTest = 0;
      this.mesh.material.transparent = this.mesh.material.opacity < 1;
      this.mesh.material.needsUpdate = true;
      return;
    }

    // Generate alpha mask with rounded corners
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // White rounded rect = opaque area, transparent corners = clipped
    const r = Math.min(radius * 60, canvas.width / 2, canvas.height / 2);
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.roundRect(0, 0, canvas.width, canvas.height, r);
    ctx.fill();

    if (this._cornerAlphaMap) this._cornerAlphaMap.dispose();
    this._cornerAlphaMap = new THREE.CanvasTexture(canvas);
    this._cornerAlphaMap.needsUpdate = true;

    this.mesh.material.alphaMap = this._cornerAlphaMap;
    this.mesh.material.alphaTest = 0.5;  // Hard clip at 50% alpha — key for clean corners
    this.mesh.material.transparent = true;
    this.mesh.material.depthWrite = true; // Prevent z-fighting
    this.mesh.material.needsUpdate = true;
  }

  update() {
    if (!this.mesh) return;
    const targetScale = this.isHovered ? 1.03 : 1.0;
    this.mesh.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.1);

    // Keep video textures updating
    if (this._videoTexture) {
      this._videoTexture.needsUpdate = true;
    }
  }
}
